import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { ConsultantLayout } from '@/shared/consultant-layout';
import { candidateProfileQueryOptions } from '../candidate-profile/candidate-profile-query';
import {
  DocumentViewerPage as DocumentViewerPageComponent,
  type DocumentViewerState,
} from './DocumentViewerPage.component';
import {
  fetchCandidateDocumentUrl,
  isDocumentMissing,
  isSharingUnconfigured,
  shareCandidateDocument,
  type DocumentKind,
  type ShareLinkResponse,
} from './document-viewer-query';
import { formatShareExpiryLabel } from './share-expiry';

/**
 * Container do visualizador de currículo/laudo (EU-594, fatia 2).
 *
 * - Reusa a query de PERFIL (nome/e-mail no header) — cache compartilhado com
 *   a tela de perfil, de onde o consultor veio.
 * - O PDF é baixado como BYTES (proxy autenticado, marca d'água server-side)
 *   → Blob → object URL, revogado no cleanup. SÓ o 404 `document_missing` do
 *   BFF vira o estado dedicado "documento não encontrado" (PFC-021); qualquer
 *   outra falha — inclusive outros 404 e o PDF.js não conseguir renderizar os
 *   bytes — é erro com "Tentar novamente" (EU-752: o frontend nunca conclui
 *   ausência a partir de falha).
 * - Modal Compartilhar: validade SEM default; "Gerar link" chama o POST de
 *   share; trocar a validade DEPOIS de gerado re-gera automaticamente
 *   (PFC-026); fechar o modal zera tudo (sem link stale).
 */
function DocumentViewerContainer({
  kind,
  userId,
  companyId,
}: {
  kind: DocumentKind;
  userId: string;
  companyId: string | undefined;
}) {
  const navigate = useNavigate();
  const profileQuery = useQuery(candidateProfileQueryOptions(userId));

  // --- download do PDF (estado próprio; object URL é recurso, não form state)
  const [docState, setDocState] = useState<DocumentViewerState>('loading');
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setDocState('loading');
    setDocumentUrl(null);
    fetchCandidateDocumentUrl(userId, kind)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setDocumentUrl(url);
        setDocState('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDocState(isDocumentMissing(err) ? 'not-found' : 'error');
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [userId, kind, reloadKey]);

  // --- modal Compartilhar
  const [isShareOpen, setShareOpen] = useState(false);
  const [ttlHours, setTtlHours] = useState<number | null>(null);
  const [shareLink, setShareLink] = useState<ShareLinkResponse | null>(null);

  const shareMutation = useMutation({
    mutationFn: (ttl: number) => shareCandidateDocument(userId, kind, ttl),
    onSuccess: (link) => setShareLink(link),
    onError: (err: unknown) => {
      // EU-752 — só o `sharing_unconfigured` do BFF ganha a copy de ambiente;
      // um 503 de ingress/pod reiniciando é "tente novamente".
      if (isSharingUnconfigured(err)) {
        toast.error('O compartilhamento de documentos não está configurado neste ambiente.');
        return;
      }
      toast.error('Não foi possível gerar o link. Tente novamente.');
    },
  });

  function handleShareOpenChange(open: boolean) {
    setShareOpen(open);
    if (!open) {
      // Reset ao fechar — reabrir nunca mostra link/validade stale (PFC-030).
      setTtlHours(null);
      setShareLink(null);
      shareMutation.reset();
    }
  }

  function handleTtlChange(ttl: number) {
    setTtlHours(ttl);
    // Já existe link → trocar a validade gera um NOVO link na hora (PFC-026).
    if (shareLink) shareMutation.mutate(ttl);
  }

  function handleGenerateLink() {
    if (ttlHours === null) return;
    shareMutation.mutate(ttlHours);
  }

  async function handleCopyLink() {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink.url);
      toast('Link copiado', { action: { label: 'Fechar', onClick: () => undefined } });
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  }

  const displayName = useMemo(() => {
    const user = profileQuery.data?.user;
    return user ? (user.socialName ?? user.name) : null;
  }, [profileQuery.data]);
  const email = profileQuery.data?.personal.email ?? profileQuery.data?.user.email ?? null;

  return (
    <ConsultantLayout activeMenuId="programs">
      <DocumentViewerPageComponent
        kind={kind}
        candidateName={displayName}
        candidateEmail={email}
        state={docState}
        documentUrl={documentUrl}
        onRetry={() => setReloadKey((k) => k + 1)}
        onPdfLoadError={() => setDocState('error')}
        onBack={() =>
          void navigate({ to: '/candidatos/$userId', params: { userId }, search: { companyId } })
        }
        onOpenShare={() => setShareOpen(true)}
        isShareOpen={isShareOpen}
        onShareOpenChange={handleShareOpenChange}
        ttlHours={ttlHours}
        onTtlChange={handleTtlChange}
        shareUrl={shareLink?.url ?? null}
        shareExpiryLabel={shareLink ? formatShareExpiryLabel(shareLink.expiresAt) : null}
        isGenerating={shareMutation.isPending}
        onGenerateLink={handleGenerateLink}
        onCopyLink={() => void handleCopyLink()}
      />
    </ConsultantLayout>
  );
}

/** Rota `/candidatos/$userId/curriculo`. */
export function ResumeViewerPage() {
  const { userId } = useParams({ from: '/_authenticated/candidatos/$userId/curriculo' });
  const { companyId } = useSearch({ from: '/_authenticated/candidatos/$userId/curriculo' });
  return <DocumentViewerContainer kind="resume" userId={userId} companyId={companyId} />;
}

/** Rota `/candidatos/$userId/laudo`. */
export function AttestationViewerPage() {
  const { userId } = useParams({ from: '/_authenticated/candidatos/$userId/laudo' });
  const { companyId } = useSearch({ from: '/_authenticated/candidatos/$userId/laudo' });
  return <DocumentViewerContainer kind="attestation" userId={userId} companyId={companyId} />;
}
